import React, { useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Slider,
  Button,
  Space,
  Row,
  Col,
  Card,
  Typography,
  Divider,
  List,
  Rate,
  message,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Exercise, MuscleGroup, EquipmentReference } from '../../types';
import { EXERCISE_TYPES, SYMMETRY_MODES } from '../../constants';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface ExerciseFormProps {
  form: any;
  exercise: Exercise | null;
  muscleGroups: MuscleGroup[];
  equipment: EquipmentReference[];
  onSave: (values: any) => Promise<void>;
  onCancel: () => void;
}

const ExerciseForm: React.FC<ExerciseFormProps> = ({
  form,
  exercise,
  muscleGroups,
  equipment,
  onSave,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (values: any) => {
    console.log('Form submitted with values:', values);
    setIsSubmitting(true);
    try {
      await onSave(values);
    } catch (error) {
      console.error('Error saving exercise:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinishFailed = (errorInfo: any) => {
    console.log('Form validation failed:', errorInfo);
    // Find the first field with an error and scroll to it
    const firstErrorField = errorInfo.errorFields?.[0];
    if (firstErrorField) {
      const fieldName = Array.isArray(firstErrorField.name) 
        ? firstErrorField.name.join('.') 
        : firstErrorField.name;
      console.log('First error field:', fieldName, firstErrorField.errors);
      
      // Show a user-friendly error message
      const fieldDisplayNames: { [key: string]: string } = {
        'name': 'Exercise Name',
        'type': 'Exercise Type', 
        'symmetryMode': 'Symmetry Mode',
        'repRange.0': 'Minimum Reps',
        'repRange.1': 'Maximum Reps',
        'instructions.0': 'First Instruction',
        'techniqueChecklist.0': 'First Technique Item'
      };
      
      const displayName = fieldDisplayNames[fieldName] || fieldName;
      const errorMessage = firstErrorField.errors?.[0] || 'Please fill this field';
      
      // Show user-friendly validation error
      message.error(`${displayName}: ${errorMessage}`);
      console.warn(`Validation Error: ${displayName} - ${errorMessage}`);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={handleFinishFailed}
      initialValues={exercise || {
        type: 'compound',
        symmetryMode: 'symmetrical',
        difficulty: 5,
        rating: 5,
        danger: 3,
        repRange: [8, 12],
        sets: 3,
        restTime: 90,
        muscleActivations: {},
        equipment: {},
        targetAdaptations: {},
        instructions: [''],
        techniqueChecklist: [''],
      }}
    >
      <Row gutter={24}>
        {/* Basic Information */}
        <Col span={24}>
          <Card title="Basic Information" size="small">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Exercise Name"
                  rules={[{ required: true, message: 'Please enter exercise name' }]}
                >
                  <Input placeholder="Enter exercise name" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="type"
                  label="Exercise Type"
                  rules={[{ required: true, message: 'Please select exercise type' }]}
                >
                  <Select>
                    {Object.values(EXERCISE_TYPES).map(type => (
                      <Option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="symmetryMode"
                  label="Symmetry Mode"
                  rules={[{ required: true, message: 'Please select symmetry mode' }]}
                >
                  <Select>
                    {Object.values(SYMMETRY_MODES).map(mode => (
                      <Option key={mode} value={mode}>
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Exercise Parameters */}
        <Col span={24}>
          <Card title="Exercise Parameters" size="small">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="repRange" label="Rep Range">
                  <Space.Compact>
                    <Form.Item
                      name={['repRange', 0]}
                      noStyle
                      rules={[{ required: true, message: 'Min reps required' }]}
                    >
                      <InputNumber
                        min={1}
                        max={100}
                        placeholder="Min"
                        style={{ width: '50%' }}
                      />
                    </Form.Item>
                    <Input
                      style={{ width: '10%', textAlign: 'center', pointerEvents: 'none' }}
                      placeholder="-"
                      disabled
                    />
                    <Form.Item
                      name={['repRange', 1]}
                      noStyle
                      rules={[{ required: true, message: 'Max reps required' }]}
                    >
                      <InputNumber
                        min={1}
                        max={100}
                        placeholder="Max"
                        style={{ width: '40%' }}
                      />
                    </Form.Item>
                  </Space.Compact>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="sets" label="Sets">
                  <InputNumber min={1} max={10} placeholder="3" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="restTime" label="Rest Time (seconds)">
                  <InputNumber min={30} max={600} placeholder="90" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name="difficulty" label="Difficulty (1-10)">
                  <Slider min={1} max={10} marks={{ 1: '1', 5: '5', 10: '10' }} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="rating" label="Rating">
                  <Rate allowHalf />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="danger" label="Danger Level (1-10)">
                  <Slider min={1} max={10} marks={{ 1: '1', 5: '5', 10: '10' }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* Muscle Activations */}
        <Col span={12}>
          <Card title="Muscle Activations (%)" size="small">
            <Form.List name="muscleActivations">
              {(fields, { add, remove }) => (
                <>
                  {muscleGroups.map(muscle => (
                    <Row key={muscle.id} gutter={8} style={{ marginBottom: 16, alignItems: 'center' }}>
                      <Col span={8}>
                        <Text strong style={{ fontSize: '13px' }}>{muscle.name}</Text>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={['muscleActivations', muscle.id]}
                          noStyle
                        >
                          <Slider 
                            min={0} 
                            max={100}
                            step={5}
                            marks={{
                              0: '0%',
                              25: '25%',
                              50: '50%',
                              75: '75%',
                              100: '100%'
                            }}
                            tooltip={{
                              formatter: (value) => `${value}%`
                            }}
                            trackStyle={{ 
                              background: 'linear-gradient(90deg, #52c41a 0%, #faad14 50%, #ff4d4f 100%)'
                            }}
                            handleStyle={{
                              borderColor: '#1890ff',
                              backgroundColor: '#1890ff',
                              boxShadow: '0 0 0 3px rgba(24, 144, 255, 0.2)'
                            }}
                            railStyle={{
                              backgroundColor: '#f5f5f5'
                            }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          shouldUpdate={(prevValues, currentValues) => 
                            prevValues.muscleActivations?.[muscle.id] !== currentValues.muscleActivations?.[muscle.id]
                          }
                          noStyle
                        >
                          {({ getFieldValue }) => (
                            <Text 
                              strong 
                              style={{ 
                                color: '#1890ff', 
                                fontSize: '14px',
                                minWidth: '35px',
                                display: 'inline-block',
                                textAlign: 'center'
                              }}
                            >
                              {getFieldValue(['muscleActivations', muscle.id]) || 0}%
                            </Text>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            </Form.List>
          </Card>
        </Col>

        {/* Equipment */}
        <Col span={12}>
          <Card title="Equipment Required" size="small">
            <Form.List name="equipment">
              {(fields, { add, remove }) => (
                <>
                  {equipment.map(eq => (
                    <Row key={eq.id} gutter={8} style={{ marginBottom: 8 }}>
                      <Col span={12}>
                        <Text>{eq.name}</Text>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name={['equipment', eq.id]}
                          noStyle
                        >
                          <InputNumber min={0} max={10} placeholder="0" />
                        </Form.Item>
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            </Form.List>
          </Card>
        </Col>

        {/* Instructions */}
        <Col span={12}>
          <Card title="Instructions" size="small">
            <Form.List name="instructions">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => {
                    const { key, ...fieldProps } = field;
                    return (
                      <Row key={key} gutter={8}>
                      <Col span={20}>
                        <Form.Item
                            {...fieldProps}
                          rules={[{ required: true, message: 'Please enter instruction' }]}
                        >
                          <Input placeholder={`Step ${index + 1}`} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                            danger
                          />
                        )}
                      </Col>
                    </Row>
                    );
                  })}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Instruction
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Col>

        {/* Technique Checklist */}
        <Col span={12}>
          <Card title="Technique Checklist" size="small">
            <Form.List name="techniqueChecklist">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Row key={field.key} gutter={8}>
                      <Col span={20}>
                        <Form.Item
                          {...field}
                          rules={[{ required: true, message: 'Please enter checklist item' }]}
                        >
                          <Input placeholder={`Technique point ${index + 1}`} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(field.name)}
                            danger
                          />
                        )}
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Checklist Item
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Col>
      </Row>

      <Divider />

      <Row justify="end">
        <Col>
          <Space>
            <Button onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
              {exercise ? 'Update Exercise' : 'Create Exercise'}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  );
};

export default ExerciseForm; 